'use es6';

import I18n from 'I18n';
import { List, Map as ImmutableMap } from 'immutable';
import { memo } from 'react';
import propertyFormatter from 'reporting-data/hydrate/propertyFormatter';
import { getReferenceLabel } from 'reporting-data/v2/dataset/datasetRecords';
import { DEFAULT_DEBUG } from '../../constants/debug';
import { PIVOT } from '../../constants/visualizationTypes';
import { getDataKeys } from '../../highcharts/chartUtils';
import pivotTransform, { TOTAL_FIELD } from '../../transforms/pivot';
import { TableVisualization } from '../table/tableRecords';
import { Render as RenderTable } from '../table/tableVisualization';
import { PivotVisualization } from './pivotRecords';
import { Render as RenderErrorMessage } from '../shared/ErrorMessage';
import { tableTooLarge } from '../shared/tableSizeLimit';
import { TABLE_TOO_LARGE } from 'reporting-data/constants/errorTypes';
import { Metrics } from '../../monitoring/Metrics';
import { TWO_DIM_TABLE_TOO_LARGE, TWO_DIM_TABLE_SUCCESSFULLY_RENDERED } from '../../constants/trackingMetrics';
import { jsx as _jsx } from "react/jsx-runtime";
export const type = PIVOT;
export const record = PivotVisualization;
export const pivotDatasetKey = 'pivot';
const mapVisualization = visualization => {
  const {
    data,
    pivot,
    events: {
      getOnPointClick
    } = {},
    showTotalsColumn,
    showTotals,
    pages
  } = visualization;
  const {
    dataset: datasetKey,
    field,
    value,
    groupby,
    op
  } = pivot;
  const dataset = data.getIn([datasetKey, 'data']);
  const pivotKeys = getDataKeys(dataset, field);
  const pivotedDataset = pivotTransform(dataset, {
    keys: pivotKeys,
    field,
    value,
    groupby,
    op,
    showTotals: showTotalsColumn
  }).update('properties', (properties = ImmutableMap()) => {
    const valueProperty = properties.get(value);
    return properties.filter((_, key) => groupby.contains(key)).merge(pivotKeys.concat(showTotalsColumn ? List.of(TOTAL_FIELD) : List()).toMap().flip().map(() => valueProperty));
  });
  const totalKey = pivot.get('groupby').first();
  const totals = pivotedDataset.get('data').reduce((accum, next) => accum.map((val, key) => val + next.get(key))).set(totalKey, 'subtotal');
  const summaryDataset = pivotedDataset.set('data', List.of(totals)).update('properties', properties => {
    return properties.map((property, key) => {
      if (key === totalKey) {
        return property;
      }
      const val = totals.get(key);
      return property.setIn(['references', String(val)], ImmutableMap({
        label: propertyFormatter(val, property, {
          currencyCode: property.getIn(['format', 'currencyCode'])
        })
      }));
    });
  });
  const columns = groupby.map(groupField => ({
    dataset: pivotDatasetKey,
    field: groupField
  })).concat(pivotKeys.map(pivotField => ({
    dataset: pivotDatasetKey,
    field: pivotField,
    label: getReferenceLabel(dataset, field, pivotField),
    getOnPointClick: getOnPointClick ? pivotedEvent => {
      const {
        column: pivotedColumn,
        row: pivotedRow,
        value: pivotedValue
      } = pivotedEvent;
      const foldedRow = pivotedRow.filter((_, key) => groupby.contains(key)).set(field, pivotedColumn.field).set(value, pivotedValue);
      const onClick = getOnPointClick(pivotedEvent);
      if (!onClick) {
        return null;
      }
      return () => onClick({
        row: foldedRow,
        field: value,
        value: pivotedValue,
        dataset,
        pivot,
        pivoted: pivotedEvent
      });
    } : undefined
  }))).concat(showTotalsColumn ? List.of({
    dataset: pivotDatasetKey,
    field: TOTAL_FIELD,
    label: I18n.text('reporting-data.properties.common.total')
  }) : List());
  const mappedVisualization = TableVisualization({
    data: {
      [pivotDatasetKey]: pivotedDataset,
      summary: summaryDataset
    },
    columns,
    pages,
    footer: 'summary',
    showTotals
  }).set('events', {
    getOnPointClick: clickEvent => {
      const {
        column: {
          getOnPointClick: getOnPointClickFunc
        }
      } = clickEvent;
      return getOnPointClickFunc ? getOnPointClickFunc(clickEvent) : null;
    }
  });
  return mappedVisualization;
};
const PivotViz = ({
  visualization,
  debug = DEFAULT_DEBUG
}) => {
  const mappedVisualization = mapVisualization(visualization);
  const columns = mappedVisualization.get('columns', List());
  const rows = mappedVisualization.getIn(['data', pivotDatasetKey, 'data'], List());
  if (tableTooLarge(rows.size, columns.size)) {
    Metrics.counter(TWO_DIM_TABLE_TOO_LARGE).increment();
    return /*#__PURE__*/_jsx(RenderErrorMessage, {
      type: TABLE_TOO_LARGE
    });
  }
  Metrics.counter(TWO_DIM_TABLE_SUCCESSFULLY_RENDERED).increment();
  return /*#__PURE__*/_jsx(RenderTable, {
    visualization: mappedVisualization,
    debug: debug
  });
};
export const Render = /*#__PURE__*/memo(PivotViz);